import { useCallback } from 'react';
import type { UseFormSetError, FieldValues, Path } from 'react-hook-form';
import { isApiError } from '@/utils/ApiError';
import { useToast } from '@/components/feedback/ToastProvider';

/**
 * Routes a failed mutation to the right place in the UI.
 *
 * Server-side field errors are attached to the corresponding inputs, so a
 * rejected e-mail address is reported on the e-mail field rather than as a
 * banner the user has to map back to a form themselves. Everything else becomes
 * a toast.
 *
 * This matters because the server validates things the client cannot: whether a
 * code is already taken, whether a grade belongs to the selected campus.
 */
export function useApiErrorHandler() {
  const toast = useToast();

  return useCallback(
    <T extends FieldValues>(error: unknown, setError?: UseFormSetError<T>) => {
      if (isApiError(error) && error.isValidation && setError) {
        for (const fieldError of error.fieldErrors) {
          setError(fieldError.field as Path<T>, { type: 'server', message: fieldError.message });
        }
        return;
      }

      if (isApiError(error)) {
        toast.error(error.message);
        return;
      }

      toast.error('Something went wrong. Please try again.');
    },
    [toast],
  );
}
